/**
 * The gap between "the connector connected" and "the client can see the turn it started".
 *
 * Connecting an app from the chat does not end the conversation: the oauth-complete endpoint hands
 * off to symphony-genome, which resolves the chat and wakes the assistant with `RunAssistantLoopAsync`.
 * That handoff is several seconds of network — verify the grant, publish `ConnectorChanged`, seed the
 * genome, then the first gateway iteration — and only the LAST of those steps stamps
 * `pendingPredictionId` on the conversation, which is the only thing `turn-activity` can read.
 *
 * So the client observes the connect (its own status poll flips the card to `connected`) well before
 * it observes the turn, and in between it renders a dead screen at exactly the moment the user has
 * just come back from a browser tab expecting something to happen. This module covers that window:
 * seeing the connect arms a local busy state, so the default thinking line shows immediately and the
 * real one from the harness replaces it as soon as a THINKING row streams in.
 *
 * Shared 1:1 with iOS `ConnectHandoff.swift` and Android `ConnectHandoff.kt` — same constant, same
 * rule, same test table. Change it here and change it there.
 */

/**
 * How long the connect alone is allowed to mean "busy".
 *
 * It is a bridge to a real signal, not a substitute for one: a genome that never greets (the connect
 * repaired an already-known toolkit, the greeting was suppressed, the service is down) must not leave
 * a spinner running forever. Long enough to outlast the handoff, short enough that a silent failure
 * reads as "nothing happened" rather than as a hang.
 */
export const CONNECT_HANDOFF_GRACE_MS = 45 * 1000;

/** Whether a connect observed at `armedAt` still counts as a turn about to start. */
export function connectHandoffPending(armedAt: number | null, now: number = Date.now()): boolean {
  if (armedAt === null) return false;
  // A clock-skewed future stamp is still live — only the past can expire.
  return now - armedAt < CONNECT_HANDOFF_GRACE_MS;
}

/**
 * How recently this browser must have tapped Connect for an already-connected card to count as
 * "the connect just happened here".
 *
 * Wider than the grace above, because it measures a different thing: the grace bounds how long a
 * spinner may run, while this bounds how long ago the user started the flow. Authorizing in another
 * tab takes as long as it takes — reading a consent screen, picking an account, a password prompt —
 * and the tap marker is the only record that it was THIS person, in THIS browser.
 */
export const CONNECT_TAP_RECENT_MS = 10 * 60 * 1000;

/**
 * Should the card's newly-observed `connected` arm the handoff?
 *
 * A TRANSITION always does: the card watched the connect land while mounted.
 *
 * The FIRST read is the case that needed fixing. `previous` is `undefined` then, so a card that comes
 * up already connected armed nothing — and coming back from the authorization tab and reloading is
 * the most ordinary thing a user does, which reset the ref and made the first read `connected: true`.
 * The result was the exact dead screen this module exists to prevent, in the one flow it was written
 * for.
 *
 * So a first read also arms, but only on evidence that the connect belongs to this person and is
 * recent: `tappedAt` is when THIS browser tapped Connect on THIS card, persisted in localStorage, so
 * it survives the reload the transition does not. Opening an old thread still spins nothing — its tap
 * marker is days old, or absent because the connect happened elsewhere.
 */
export function shouldArmConnectHandoff(
  previous: boolean | undefined,
  connected: boolean,
  tappedAt: number | null = null,
  now: number = Date.now(),
): boolean {
  if (!connected) {
    return false;
  }
  if (previous === false) {
    return true;
  }
  return previous === undefined && tappedAt !== null && now - tappedAt < CONNECT_TAP_RECENT_MS;
}
